// utils/recurringPaymentsCsvValidator.js

import Papa from 'papaparse';

/**
 * Validador CSV para actualización masiva de montos en Recurring Payments
 * Usa PapaParse con Worker threads para no bloquear la UI
 */
export class RecurringPaymentsCsvValidator {
  constructor() {
    // Columnas requeridas en el orden exacto de la plantilla
    this.requiredFields = ['ID', 'EMAIL', 'NOMBRE', 'DOCUMENTO', 'MONTO ANTERIOR', 'MONTO ACTUAL', 'FECHA PRÓXIMO COBRO', 'IDENTIFICADOR'];
    
    // ✅ Límites de seguridad
    this.MAX_FILE_SIZE_MB = 10; // Máximo 10 MB
    this.MAX_ROWS = 100000; // Máximo 100,000 registros
    
    // ✅ Regexps compilados (reutilizables) - Optimización de performance
    this.UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    // UUID (36 chars) + underscore + check digit (2 chars) = 39 chars total
    this.ID_CHECK_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_[0-9a-f]{2}$/i;
    // Acepta números con coma o punto como separador decimal
    this.AMOUNT_REGEX = /^\d+([,.]\d{1,2})?$/;
    // PR-135: Máximo 9 dígitos totales (entero + decimal)
    this.MAX_AMOUNT_DIGITS = 9;
    // Identificador externo: solo alfanuméricos y caracteres especiales permitidos (según entidad Subscription)
    this.EXTERNAL_REF_REGEX = /^[A-Za-z0-9._:/-]+$/;
  }

  /**
   * Genera dígito verificador usando Módulo 11 (similar a CUIT)
   * Debe coincidir con el algoritmo del backend
   * @param {string} id - ID de suscripción (UUID)
   * @returns {string} Check digit de 2 caracteres hex
   */
  generateCheckDigit(id) {
    const hex = id.replace(/-/g, '');
    let sum = 0;
    let multiplier = 2;
    
    for (let i = hex.length - 1; i >= 0; i--) {
      const digit = parseInt(hex[i], 16);
      sum += digit * multiplier;
      multiplier = (multiplier === 7) ? 2 : multiplier + 1;
    }
    
    const remainder = sum % 11;
    const check = (11 - remainder) % 11;
    
    return check.toString(16).padStart(2, '0');
  }

  /**
   * Valida un archivo CSV usando PapaParse con Worker threads y modo chunk
   * @param {File} file - Archivo CSV
   * @param {Function} onProgress - Callback de progreso (0-100)
   * @returns {Promise<ValidationResult>}
   */
  async validateFile(file, onProgress) {
    // ✅ VALIDACIÓN 1: Verificar tamaño del archivo
    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > this.MAX_FILE_SIZE_MB) {
      return Promise.reject(new Error(
        `El archivo es demasiado grande (${fileSizeInMB.toFixed(2)} MB). ` +
        `El tamaño máximo permitido es ${this.MAX_FILE_SIZE_MB} MB (aproximadamente ${(this.MAX_ROWS / 1000).toFixed(0)}k registros).`
      ));
    }
    
    return new Promise((resolve, reject) => {
      const errors = [];
      let validRows = 0;
      let totalRows = 0;
      let header = null;
      const MAX_ERRORS_TO_SHOW = 50;
      let totalErrorRows = 0;
      let processedChunks = 0;
      let estimatedTotalChunks = 0;
      
      // Array para trackear promesas de validación de chunks
      const chunkValidationPromises = [];

      Papa.parse(file, {
        // ⚠️ Sin worker thread para evitar CSP issues
        // Los chunks se procesan en el thread principal pero de forma asíncrona
        worker: false,
        
        // ✅ Modo chunk: procesa en lotes de 5 MB
        chunkSize: 1024 * 1024 * 5, // 5 MB por chunk
        
        // ✅ Callback por chunk (en lugar de por fila)
        chunk: (results, parser) => {
          processedChunks++;
          const chunkData = results.data;

          // Estimar total de chunks (primera vez)
          if (estimatedTotalChunks === 0 && file.size > 0) {
            estimatedTotalChunks = Math.ceil(file.size / (1024 * 1024 * 5));
          }

          // Si es el primer chunk, extraer y validar header
          if (!header && chunkData.length > 0) {
            header = chunkData[0];
            
            // Validar que tenga EXACTAMENTE las columnas requeridas
            const hasAllColumns = this.requiredFields.every(field => header.includes(field));
            const hasExtraColumns = header.some(col => !this.requiredFields.includes(col));
            
            if (!hasAllColumns || hasExtraColumns || header.length !== this.requiredFields.length) {
              errors.push({
                row: 1,
                field: 'header',
                error: 'El archivo a cargar debe tener las mismas columnas que la plantilla descargada, de lo contrario, no se podrá procesar.',
                lineContent: header.join(',')
              });
              parser.abort();
              return;
            }
            
            // Remover header del chunk para no validarlo como dato
            chunkData.shift();
          }

          // ✅ Validar todas las filas del chunk en batch (async)
          const chunkPromise = (async () => {
            const rowsToValidate = [];
            let currentRowIndex = totalRows;

            // Preparar todas las filas para validar
            for (let i = 0; i < chunkData.length; i++) {
              const row = chunkData[i];
              
              // Saltar filas vacías
              if (!row || row.length === 0 || row.every(cell => !cell || cell.trim() === '')) {
                continue;
              }

              currentRowIndex++;
              
              // ✅ VALIDACIÓN 2: Verificar límite de registros
              if (currentRowIndex > this.MAX_ROWS) {
                parser.abort();
                reject(new Error(
                  `El archivo tiene demasiados registros (>${this.MAX_ROWS.toLocaleString()}). ` +
                  `Por favor, dividí el archivo en lotes más pequeños.`
                ));
                return;
              }

              rowsToValidate.push({ row, rowNum: currentRowIndex });
            }

            // Validar todas las filas (ahora síncrono, mucho más rápido)
            try {
              rowsToValidate.forEach(({ row, rowNum }) => {
                const rowErrors = this.validateRow(row, header, rowNum);
                
                if (rowErrors.length > 0) {
                  // Solo guardar los primeros N errores para no sobrecargar memoria
                  if (errors.length < MAX_ERRORS_TO_SHOW) {
                    errors.push(...rowErrors);
                  }
                  totalErrorRows++;
                } else {
                  validRows++;
                }
                totalRows++;
              });
            } catch (validationError) {
              parser.abort();
              reject(new Error(`Error durante la validación: ${validationError.message}`));
            }
          })();
          
          // Agregar la promesa al array para esperar
          chunkValidationPromises.push(chunkPromise);

          // ✅ Actualizar progreso por chunk procesado
          if (onProgress) {
            const progress = Math.min(95, (processedChunks / Math.max(1, estimatedTotalChunks)) * 100);
            onProgress(Math.round(progress));
          }
        },
        
        // Configuración de parseo
        delimiter: '',      // Auto-detect
        newline: '',        // Auto-detect
        quoteChar: '"',
        escapeChar: '"',
        skipEmptyLines: true,
        
        // Callback al completar todo el archivo
        complete: async () => {
          // Esperar a que todas las validaciones de chunks terminen
          await Promise.all(chunkValidationPromises);
          
          if (onProgress) onProgress(100);
          
          resolve({
            isValid: errors.length === 0 && totalErrorRows === 0,
            errors: errors.slice(0, MAX_ERRORS_TO_SHOW),
            hasMoreErrors: totalErrorRows > errors.length,
            totalErrorRows,
            validRows,
            totalRows
          });
        },

        // Callback de error
        error: (error) => {
          reject(new Error(`Error parseando CSV: ${error.message}`));
        }
      });
    });
  }

  /**
   * Valida una fila individual con el nuevo formato de columnas
   * @param {Array} row - Datos de la fila
   * @param {Array} header - Nombres de columnas
   * @param {number} rowNum - Número de fila
   * @returns {Array<ValidationError>}
   */
  validateRow(row, header, rowNum) {
    const errors = [];
    const lineContent = row.join(',');

    // Crear objeto fila para acceso fácil
    const rowObj = {};
    header.forEach((field, index) => {
      rowObj[field] = row[index] || '';
    });

    // ✅ VALIDACIÓN 1: ID (UUID_CHECK) - REQUERIDO
    const idWithCheck = rowObj['ID']?.trim() || '';
    
    if (!idWithCheck) {
      errors.push({
        row: rowNum,
        field: 'ID',
        error: 'El ID es requerido, no lo debes modificar',
        lineContent
      });
    } else if (!this.ID_CHECK_REGEX.test(idWithCheck)) {
      errors.push({
        row: rowNum,
        field: 'ID',
        error: 'El ID tiene un formato inválido. Descargá nuevamente la plantilla.',
        lineContent
      });
    } else {
      // Separar ID y check digit para validar integridad
      const [id, checkDigit] = idWithCheck.split('_');
      const expectedCheck = this.generateCheckDigit(id);
      if (checkDigit !== expectedCheck) {
        errors.push({
          row: rowNum,
          field: 'ID',
          error: 'El ID fue modificado. Descargá nuevamente la plantilla y no modifiques la columna ID.',
          lineContent
        });
      }
    }

    // ✅ VALIDACIÓN 2: MONTO ACTUAL - REQUERIDO, debe ser positivo > 0
    const montoActual = rowObj['MONTO ACTUAL']?.trim() || '';
    
    if (!montoActual) {
      errors.push({
        row: rowNum,
        field: 'MONTO ACTUAL',
        error: 'El monto actual es requerido',
        lineContent
      });
    } else if (!this.AMOUNT_REGEX.test(montoActual)) {
      errors.push({
        row: rowNum,
        field: 'MONTO ACTUAL',
        error: 'Monto inválido (debe ser un número positivo con máximo 2 decimales, ejemplo: 1250,50 o 1250.50)',
        lineContent
      });
    } else if ((montoActual.replace(/\D/g, '') || '').length > this.MAX_AMOUNT_DIGITS) {
      errors.push({
        row: rowNum,
        field: 'MONTO ACTUAL',
        error: `El monto no puede tener más de ${this.MAX_AMOUNT_DIGITS} dígitos`,
        lineContent
      });
    } else {
      // Convertir coma a punto para parseFloat
      const normalizedAmount = montoActual.replace(',', '.');
      const amount = parseFloat(normalizedAmount);
      
      if (isNaN(amount)) {
        errors.push({
          row: rowNum,
          field: 'MONTO ACTUAL',
          error: 'El monto debe ser un número válido',
          lineContent
        });
      } else if (amount < 0) {
        errors.push({
          row: rowNum,
          field: 'MONTO ACTUAL',
          error: 'El monto no puede ser negativo',
          lineContent
        });
      } else if (amount > 999999999.99) {
        errors.push({
          row: rowNum,
          field: 'MONTO ACTUAL',
          error: 'El monto es demasiado grande (máximo: 999999999,99)',
          lineContent
        });
      }
    }

    // ✅ VALIDACIÓN 3: IDENTIFICADOR - OPCIONAL, máximo 128 caracteres, solo alfanuméricos y ._:/-
    const identificador = rowObj['IDENTIFICADOR']?.trim() || '';
    
    if (identificador) {
      if (identificador.length > 128) {
        errors.push({
          row: rowNum,
          field: 'IDENTIFICADOR',
          error: 'El identificador no puede tener más de 128 caracteres',
          lineContent
        });
      } else if (!this.EXTERNAL_REF_REGEX.test(identificador)) {
        errors.push({
          row: rowNum,
          field: 'IDENTIFICADOR',
          error: 'El identificador solo puede contener letras, números y los caracteres: . _ : / -',
          lineContent
        });
      }
    }

    return errors;
  }

  /**
   * Formatea errores para mostrar en UI
   */
  formatErrorReport(result) {
    if (result.isValid) {
      return {
        isValid: true,
        summary: `✅ Archivo válido: ${result.validRows} suscripciones para actualizar`,
        validRows: result.validRows,
        totalRows: result.totalRows
      };
    }

    // Agrupar errores por fila
    const errorsByRow = {};
    result.errors.forEach(error => {
      if (!errorsByRow[error.row]) {
        errorsByRow[error.row] = [];
      }
      errorsByRow[error.row].push(error);
    });

    const totalErrorRowsShown = Object.keys(errorsByRow).length;
    const hasMoreErrors = result.hasMoreErrors || result.totalErrorRows > totalErrorRowsShown;

    return {
      isValid: false,
      summary: hasMoreErrors 
        ? `❌ Se encontraron ${result.totalErrorRows}+ errores (mostrando primeros 50)`
        : `❌ ${result.totalErrorRows} errores encontrados en ${totalErrorRowsShown} filas`,
      errorsByRow,
      totalErrors: result.errors.length,
      totalErrorRows: result.totalErrorRows,
      hasMoreErrors,
      validRows: result.validRows,
      totalRows: result.totalRows
    };
  }

  /**
   * Genera un CSV de ejemplo para download
   */
  generateTemplate(subscriptions = []) {
    const rows = [
      ['subscription_id', 'new_amount', 'effective_date']
    ];

    if (subscriptions.length > 0) {
      // Agregar suscripciones reales
      subscriptions.forEach(sub => {
        rows.push([
          sub.id || sub.subscription_id,
          sub.amount || '0.00',
          '' // Opcional
        ]);
      });
    } else {
      // Agregar ejemplos
      rows.push([
        '12345678-1234-1234-1234-123456789012',
        '1250.00',
        '2024-12-31'
      ]);
      rows.push([
        '87654321-4321-4321-4321-210987654321',
        '2500.50',
        ''
      ]);
    }

    return Papa.unparse(rows);
  }
}

